import { InstagramPreview } from 'components/Previews/InstagramPreview/InstagramPreview'
import { XPreview } from 'components/Previews/XPreview/XPreview'
import { FacebookPreview } from 'components/Previews/FacebookPreview/FacebookPreview'
import { LinkedinPreview } from 'components/Previews/LinkedinPreview/LinkedinPreview'
import {
  LinkedInIntegration,
  FacebookIntegration,
  InstagramIntegration,
  XIntegration,
  TikTokIntegration,
  YouTubeIntegration,
  GoogleBusinessProfileIntegration,
  ExternalIntegrationEnum,
} from 'api/clients/ExternalIntegrationsClient'
import { DesignChannel } from 'components/NewFileModal/Types'
import { InstagramStoryPreview } from 'components/Previews/InstagramStoryPreview/InstagramStoryPreview'
import { FacebookStoryPreview } from 'components/Previews/FacebookStoryPreview/FacebookStoryPreview'
import { PreviewProps } from './Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { TikTokPreview } from 'components/Previews/TikTokPreview/TikTokPreview'
import { InstagramReelPreview } from 'components/Previews/InstagramReelPreview/InstagramReelPreview'
import { YouTubeShortPreview } from 'components/Previews/YoutubeShortPreview/YouTubeShortPreview'
import { UPLOAD_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { GoogleBusinessProfilePreview } from 'components/Previews/GoogleBusinessProfilePreview/GoogleBusinessProfilePreview'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'

export const Preview = ({
  channel,
  loading,
  post,
  integration,
  pollPreviewContentStream = true,
  hideConnectButton = false,
  onEdit,
  containerClassName,
  photoBoxClassName,
  customPreviewContent,
  maxWidth,
  showVideoControls = false,
  videoFit,
  isActive = true,
  isDesignBlank = false,
  isInsertingMedia = false,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  onClose,
  alwaysTruncateCaption = false,
  usernameContentSameLine = false,
  postPreviewHeaderRef,
  postPreviewContentRef,
  postPreviewFooterRef,
  activePlatform,
  onSeeMoreToggle,
}: PreviewProps) => {
  const dynamicCaptionsEnabled = useFlipperFlag('dynamic_captions')
  const previewProvider = activePlatform ?? providerFromChannelType[channel]
  const dynamicContent =
    dynamicCaptionsEnabled && previewProvider
      ? post?.scheduled_post_targets?.find((target) => target.provider === previewProvider)?.target_overrides?.content
      : undefined
  const content = dynamicContent || post?.content || ''
  const mediaObjects = isDesignBlank ? [] : post?.media_objects

  const postPreviewRefs = {
    postPreviewHeaderRef,
    postPreviewContentRef,
    postPreviewFooterRef,
  }

  switch (channel) {
    case DesignChannel.LinkedInPost:
    case ChannelType.LinkedInPosts:
      return (
        <LinkedinPreview
          loading={loading}
          integration={integration as LinkedInIntegration}
          content={content}
          mediaObjects={mediaObjects}
          hideConnectButton={hideConnectButton}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          maxWidth={maxWidth}
          showVideoControls={showVideoControls}
          videoFit={videoFit}
          isActive={isActive}
          isInsertingMedia={isInsertingMedia}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          scheduledPost={post}
          onClose={onClose}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
          onSeeMoreToggle={onSeeMoreToggle}
          activePlatform={activePlatform}
          {...(activePlatform === ExternalIntegrationEnum.LinkedIn ? postPreviewRefs : {})}
        />
      )
    case DesignChannel.InstagramPost:
    case ChannelType.InstagramPosts:
      return (
        <InstagramPreview
          loading={loading}
          integration={integration as InstagramIntegration}
          content={content}
          mediaObjects={mediaObjects}
          pollContentStream={pollPreviewContentStream}
          hideConnectButton={hideConnectButton}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          maxWidth={maxWidth}
          showVideoControls={showVideoControls}
          videoFit={videoFit}
          isActive={isActive}
          isInsertingMedia={isInsertingMedia}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          onClose={onClose}
          scheduledPost={post}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
          usernameContentSameLine={usernameContentSameLine}
          onSeeMoreToggle={onSeeMoreToggle}
          activePlatform={activePlatform}
          {...(activePlatform === ExternalIntegrationEnum.Instagram ? postPreviewRefs : {})}
        />
      )
    case DesignChannel.InstagramReel:
      const hasSingleNonVideoMedia =
        mediaObjects?.length === 1 && mediaObjects[0] && !UPLOAD_VIDEO_MIME_TYPES.includes(mediaObjects[0].mime_type)

      if (hasSingleNonVideoMedia) {
        return (
          <InstagramPreview
            loading={loading}
            integration={integration as InstagramIntegration}
            content={content}
            mediaObjects={mediaObjects}
            pollContentStream={pollPreviewContentStream}
            hideConnectButton={hideConnectButton}
            onEdit={onEdit}
            containerClassName={containerClassName}
            customPreviewContent={customPreviewContent}
            maxWidth={maxWidth}
            showVideoControls={showVideoControls}
            videoFit={videoFit}
            isActive={isActive}
            isInsertingMedia={isInsertingMedia}
            isTransitioning={isTransitioning}
            isGenerating={isGenerating}
            onClose={onClose}
            alwaysTruncateCaption={alwaysTruncateCaption}
            onSeeMoreToggle={onSeeMoreToggle}
            activePlatform={activePlatform}
            {...(activePlatform === ExternalIntegrationEnum.Instagram ? postPreviewRefs : {})}
          />
        )
      }

      return (
        <InstagramReelPreview
          integration={integration as InstagramIntegration}
          content={content}
          mediaObjects={mediaObjects}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          isGenerating={isGenerating}
          onClose={onClose}
          alwaysTruncateCaption={alwaysTruncateCaption}
        />
      )
    case DesignChannel.InstagramStory:
      return (
        <InstagramStoryPreview
          integration={integration as InstagramIntegration}
          mediaObject={mediaObjects?.[0]}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          onClose={onClose}
          isGenerating={isGenerating}
        />
      )
    case DesignChannel.FacebookStory:
      return (
        <FacebookStoryPreview
          integration={integration as FacebookIntegration}
          mediaObject={mediaObjects?.[0]}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          onClose={onClose}
          isGenerating={isGenerating}
        />
      )
    case DesignChannel.TwitterPost:
    case ChannelType.Tweets:
      return (
        <XPreview
          loading={loading}
          integration={integration as XIntegration}
          content={content}
          mediaObjects={mediaObjects}
          hideConnectButton={hideConnectButton}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          maxWidth={maxWidth}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isInsertingMedia={isInsertingMedia}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          scheduledPost={post}
          onClose={onClose}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
          onSeeMoreToggle={onSeeMoreToggle}
          activePlatform={activePlatform}
          {...(activePlatform === ExternalIntegrationEnum.X ? postPreviewRefs : {})}
        />
      )
    case DesignChannel.FacebookPost:
    case ChannelType.FacebookPost:
      return (
        <FacebookPreview
          loading={loading}
          integration={integration as FacebookIntegration}
          content={content}
          mediaObjects={mediaObjects}
          pollContentStream={pollPreviewContentStream}
          hideConnectButton={hideConnectButton}
          onEdit={onEdit}
          containerClassName={containerClassName}
          photoBoxClassName={photoBoxClassName}
          customPreviewContent={customPreviewContent}
          maxWidth={maxWidth}
          showVideoControls={showVideoControls}
          videoFit={videoFit}
          isActive={isActive}
          isInsertingMedia={isInsertingMedia}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          scheduledPost={post}
          onClose={onClose}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
          onSeeMoreToggle={onSeeMoreToggle}
          activePlatform={activePlatform}
          {...(activePlatform === ExternalIntegrationEnum.Facebook ? postPreviewRefs : {})}
        />
      )
    case DesignChannel.TikTokPost:
      return (
        <TikTokPreview
          loading={loading}
          integration={integration as TikTokIntegration}
          content={content}
          mediaObjects={mediaObjects}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          onClose={onClose}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
        />
      )
    case DesignChannel.YoutubeShort:
      return (
        <YouTubeShortPreview
          integration={integration as YouTubeIntegration}
          post={post}
          mediaObjects={mediaObjects}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          onClose={onClose}
          isGenerating={isGenerating}
        />
      )
    case DesignChannel.GoogleMyBusinessPost:
      return (
        <GoogleBusinessProfilePreview
          loading={loading}
          integration={integration as GoogleBusinessProfileIntegration}
          content={content}
          mediaObjects={mediaObjects}
          pollContentStream={pollPreviewContentStream}
          onEdit={onEdit}
          containerClassName={containerClassName}
          customPreviewContent={customPreviewContent}
          maxWidth={maxWidth}
          showVideoControls={showVideoControls}
          isActive={isActive}
          isInsertingMedia={isInsertingMedia}
          isTransitioning={isTransitioning}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          scheduledPost={post}
          onClose={onClose}
          isGenerating={isGenerating}
          alwaysTruncateCaption={alwaysTruncateCaption}
          onSeeMoreToggle={onSeeMoreToggle}
          activePlatform={activePlatform}
          {...(activePlatform === ExternalIntegrationEnum.GoogleBusinessProfile ? postPreviewRefs : {})}
        />
      )
    default:
      return null
  }
}
